import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Analytical Applications of Differentiation",
  examWeight: "15–18%",
  focus: "Analysis of Functions, Limits",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: The Mean Value Theorem (MVT)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The MVT connects the <strong>average rate of change</strong> (secant slope) over an interval to the <strong>instantaneous rate of change</strong> (tangent slope) at a specific point. Geometrically, it guarantees a tangent line parallel to the secant line.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Theorem</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If a function <LatexText text="$f$" /> is:
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>1. Continuous</strong> on the closed interval <LatexText text="$[a, b]$" /> AND</p>
              <p className="text-slate-700"><strong>2. Differentiable</strong> on the open interval <LatexText text="$(a, b)$" />,</p>
            </div>
            <p className="text-slate-700 leading-relaxed mb-4">
              Then there exists at least one number <LatexText text="$c$" /> in <LatexText text="$(a, b)$" /> such that:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <LatexText text="$$f'(c) = \frac{f(b) - f(a)}{b - a}$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rolle's Theorem</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A special case of MVT. If <LatexText text="$f(a) = f(b)$" /> (the start and end heights are the same), then the average rate of change is 0. Therefore, there must be a point <LatexText text="$c$" /> where <LatexText text="$f'(c) = 0$" /> (a horizontal tangent).
            </p>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> When asked to justify if a derivative equals a specific number, check the MVT conditions first. You <strong>must</strong> state "Since <LatexText text="$f$" /> is continuous on <LatexText text="$[a, b]$" /> and differentiable on <LatexText text="$(a, b)$" />..." to earn full credit.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Extreme Value Theorem, Global vs. Local Extrema, and Critical Points",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Extreme Value Theorem (EVT)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$f$" /> is <strong>continuous</strong> on a <strong>closed interval</strong> <LatexText text="$[a, b]$" />, then <LatexText text="$f$" /> MUST have both an absolute (global) maximum and an absolute (global) minimum on that interval.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Critical Points</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A Critical Point occurs at <LatexText text="$x = c$" /> if:
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong><LatexText text="$f'(c) = 0$" /></strong> (Horizontal Tangent) <strong>OR</strong></p>
              <p className="text-slate-700"><strong><LatexText text="$f'(c)$" /> is undefined</strong> (Sharp turn/cusp or Vertical Tangent), provided <LatexText text="$c$" /> is in the domain of <LatexText text="$f$" />.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Extrema</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Local (Relative) Extrema</h4>
                <p className="text-sm text-slate-700 mt-1">The highest/lowest point in the immediate neighborhood (peaks and valleys). These <em>only</em> occur at critical points.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Global (Absolute) Extrema</h4>
                <p className="text-sm text-slate-700 mt-1">The highest/lowest point on the entire domain. These occur at critical points OR endpoints.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Determining Intervals on Which a Function is Increasing or Decreasing",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The First Derivative Test for Behavior</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The sign of the first derivative tells us the direction of the function.
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>If <LatexText text="$f'(x) > 0$" /> on an interval,</strong> <LatexText text="$f(x)$" /> is <strong>Increasing</strong>.</p>
              <p className="text-slate-700"><strong>If <LatexText text="$f'(x) < 0$" /> on an interval,</strong> <LatexText text="$f(x)$" /> is <strong>Decreasing</strong>.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Justification Syntax</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700"><strong>Correct:</strong> "<LatexText text="$f$" /> is increasing on <LatexText text="$(2, 5)$" /> because <LatexText text="$f'(x) > 0$" />."</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700"><strong>Incorrect:</strong> "<LatexText text="$f$" /> is increasing because the slope is going up." (Vague).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Using the First Derivative Test to Determine Relative (Local) Extrema",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To find relative max/min:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find <LatexText text="$f'(x)$" /></h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find Critical Points</h4>
                  <p className="text-sm text-slate-600 mt-1">(where <LatexText text="$f'=0$" /> or undef)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Place points on a Sign Chart</h4>
                  <p className="text-sm text-slate-600 mt-1">(number line)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Test numbers between critical points</h4>
                  <p className="text-sm text-slate-600 mt-1">to see if <LatexText text="$f'$" /> is pos or neg</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Analyzing the Changes</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Relative Maximum</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs if <LatexText text="$f'$" /> changes from <strong>Positive to Negative</strong>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Relative Minimum</h4>
                <p className="text-sm text-slate-700 mt-1">Occurs if <LatexText text="$f'$" /> changes from <strong>Negative to Positive</strong>.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">Neither</h4>
                <p className="text-sm text-slate-700 mt-1">If <LatexText text="$f'$" /> does not change signs (e.g., pos to pos), it is a "terrace point," not an extremum.</p>
              </div>
            </div>

            <ImportantNote>
              <strong>AP Exam Tip:</strong> A Sign Chart helps you organize your thoughts, but it is <strong>NOT</strong> sufficient justification for the AP exam. You must write a sentence: "There is a relative max at <LatexText text="$x=c$" /> because <LatexText text="$f'$" /> changes from positive to negative."
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Using the Candidates Test to Determine Absolute (Global) Extrema",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">When to use it</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Use this when asked for the <strong>Absolute</strong>, <strong>Global</strong>, <strong>Max</strong>, or <strong>Min</strong> on a <strong>closed interval</strong>.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Candidates Test Procedure</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find all Critical Points</h4>
                  <p className="text-sm text-slate-600 mt-1">(<LatexText text="$c$" />) inside the interval</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Identify the Endpoints</h4>
                  <p className="text-sm text-slate-600 mt-1">(<LatexText text="$a$" /> and <LatexText text="$b$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Make a Table of Values</h4>
                  <p className="text-sm text-slate-600 mt-1">(<LatexText text="$x$" /> vs <LatexText text="$f(x)$" />)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Plug in critical points AND endpoints</h4>
                  <p className="text-sm text-slate-600 mt-1">into the <strong>original function</strong> <LatexText text="$f(x)$" /> (not the derivative)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Compare the <LatexText text="$y$" />-values</h4>
                  <p className="text-sm text-slate-600 mt-1">The highest is the Abs Max; the lowest is the Abs Min</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Determining Concavity of Functions over Their Domains",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concavity Definitions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Concavity describes how the curve bends.
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Concave Up</h4>
                <p className="text-sm text-slate-700 mt-1">The graph opens upward (like a cup). Tangent lines are <em>below</em> the curve. <LatexText text="$f'$" /> is increasing.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Concave Down</h4>
                <p className="text-sm text-slate-700 mt-1">The graph opens downward (like a frown). Tangent lines are <em>above</em> the curve. <LatexText text="$f'$" /> is decreasing.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Second Derivative Link</h3>
            <div className="space-y-2 mb-4">
              <p className="text-slate-700"><strong>If <LatexText text="$f''(x) > 0$" />,</strong> <LatexText text="$f$" /> is Concave Up.</p>
              <p className="text-slate-700"><strong>If <LatexText text="$f''(x) < 0$" />,</strong> <LatexText text="$f$" /> is Concave Down.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Points of Inflection (POI)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A Point of Inflection occurs where the <strong>concavity changes</strong>.
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Condition 1:</strong> <LatexText text="$f''(c) = 0$" /> or is undefined.</p>
              <p className="text-slate-700"><strong>Condition 2:</strong> <LatexText text="$f''$" /> <strong>changes signs</strong> at <LatexText text="$c$" /> (from pos to neg or neg to pos).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-7",
      title: "Topic 5.7: Using the Second Derivative Test to Determine Extrema",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Purpose</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This is an alternative method to the First Derivative Test for finding Local Max/Mins. It is useful when analyzing a specific point or when <LatexText text="$f'$" /> is difficult to solve for intervals.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Logic</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Relative Maximum</h4>
                <p className="text-sm text-slate-700 mt-1">If <LatexText text="$f'(c) = 0$" /> (flat tangent) AND <LatexText text="$f''(c) < 0$" /> (concave down/frown), then <LatexText text="$c$" /> is a <strong>Relative Maximum</strong>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Relative Minimum</h4>
                <p className="text-sm text-slate-700 mt-1">If <LatexText text="$f'(c) = 0$" /> (flat tangent) AND <LatexText text="$f''(c) > 0$" /> (concave up/cup), then <LatexText text="$c$" /> is a <strong>Relative Minimum</strong>.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Inconclusive</h4>
                <p className="text-sm text-slate-700 mt-1">If <LatexText text="$f''(c) = 0$" />, the test is <strong>Inconclusive</strong>. (You must go back and use the First Derivative Test).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-8",
      title: "Topics 5.8 & 5.9: Sketching Graphs & Connecting f, f', and f''",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Relationship Hierarchy</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Understanding the relationships between the graphs is the most tested concept in Unit 5.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left">Feature of <LatexText text="$f(x)$" /></th>
                    <th className="border border-slate-300 p-2 text-left">Behavior of <LatexText text="$f'(x)$" /></th>
                    <th className="border border-slate-300 p-2 text-left">Behavior of <LatexText text="$f''(x)$" /></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2">Rising / Increasing</td>
                    <td className="border border-slate-300 p-2">Positive (<LatexText text="$>0$" />)</td>
                    <td className="border border-slate-300 p-2">—</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2">Falling / Decreasing</td>
                    <td className="border border-slate-300 p-2">Negative (<LatexText text="$<0$" />)</td>
                    <td className="border border-slate-300 p-2">—</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2">Relative Max</td>
                    <td className="border border-slate-300 p-2">Changes Pos to Neg</td>
                    <td className="border border-slate-300 p-2">—</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2">Relative Min</td>
                    <td className="border border-slate-300 p-2">Changes Neg to Pos</td>
                    <td className="border border-slate-300 p-2">—</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2">Concave Up</td>
                    <td className="border border-slate-300 p-2">Increasing (slope is rising)</td>
                    <td className="border border-slate-300 p-2">Positive (<LatexText text="$>0$" />)</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2">Concave Down</td>
                    <td className="border border-slate-300 p-2">Decreasing (slope is falling)</td>
                    <td className="border border-slate-300 p-2">Negative (<LatexText text="$<0$" />)</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2">Point of Inflection</td>
                    <td className="border border-slate-300 p-2">Relative Max/Min (peak/valley)</td>
                    <td className="border border-slate-300 p-2">Changes Sign</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Reading the Graph of <LatexText text="$f'$" /> (The Derivative Graph)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When given a graph of <LatexText text="$f'$" /> (not <LatexText text="$f$" />):
            </p>
            <div className="space-y-2">
              <p className="text-slate-700"><strong>Y-values of the graph</strong> tell you if <LatexText text="$f$" /> is increasing/decreasing.</p>
              <p className="text-slate-700"><strong>X-intercepts</strong> are the critical points (possible max/mins of <LatexText text="$f$" />).</p>
              <p className="text-slate-700"><strong>Slopes of the graph</strong> tell you the concavity of <LatexText text="$f$" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-10",
      title: "Topics 5.10 & 5.11: Optimization Problems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Optimization is the application of finding Absolute Maxima/Minima in real-life contexts (e.g., maximizing profit, minimizing material cost, maximizing area).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Procedure</h3>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Identify the Objective Function</h4>
                  <p className="text-sm text-slate-600 mt-1">What are you trying to maximize or minimize? Write an equation for it (e.g., <LatexText text="$A = xy$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Identify the Constraint</h4>
                  <p className="text-sm text-slate-600 mt-1">What is limiting you? Write an equation (e.g., Perimeter <LatexText text="$2x + 2y = 100$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Substitution</h4>
                  <p className="text-sm text-slate-600 mt-1">Solve the constraint for one variable and plug it into the objective function so it is in terms of <strong>one single variable</strong>.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Differentiate</h4>
                  <p className="text-sm text-slate-600 mt-1">Find the derivative of the new objective function.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Critical Points</h4>
                  <p className="text-sm text-slate-600 mt-1">Set the derivative to 0 and solve.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">6</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Verify</h4>
                  <p className="text-sm text-slate-600 mt-1">Use the First Derivative Test or Candidates Test to ensure you found the max or min, not the wrong one.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">7</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Answer the Question</h4>
                  <p className="text-sm text-slate-600 mt-1">Check if the problem asks for the x-value, the y-value, or the resulting maximum area/cost.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-12",
      title: "Topic 5.12: Exploring Behaviors of Implicit Relations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Implicit Analysis</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Applying the concepts of Unit 5 to non-functions (circles, ellipses, etc.) using implicit differentiation.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Features</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Given an implicit derivative <LatexText text="$\frac{dy}{dx} = \frac{N(x,y)}{D(x,y)}$" />:
            </p>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Horizontal Tangents</h4>
                <p className="text-sm text-slate-700 mt-1">Occur when the Numerator <LatexText text="$N(x,y) = 0$" /> (and Denominator <LatexText text="$\neq 0$" />).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Vertical Tangents</h4>
                <p className="text-sm text-slate-700 mt-1">Occur when the Denominator <LatexText text="$D(x,y) = 0$" /> (and Numerator <LatexText text="$\neq 0$" />).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Second Derivative</h4>
                <p className="text-sm text-slate-700 mt-1">When finding <LatexText text="$\frac{d^2y}{dx^2}$" /> implicitly to check concavity, remember to substitute the expression for <LatexText text="$\frac{dy}{dx}$" /> back into the equation.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit5Notes;